import React, { useState } from 'react';
import { useQuery } from 'react-apollo';
import PropTypes from 'prop-types';
import {
  Card as ReactstrapCard,
  CardHeader as ReactstrapCardHeader,
  CardBody,
  Collapse,
  Spinner,
  Table,
  Alert
} from 'reactstrap';
import moment from 'moment';
import styled from 'styled-components';
import { GET_DOCTOR_CHARGES } from '../queries';
import { FaRupeeSign, FaMinus, FaPlus } from 'react-icons/fa';
import { RupeeContainer } from './DoctorCharges';

const Card = styled(ReactstrapCard)`
  font-weight: bold;
  font-size: 0.85rem;
`;

const Header = styled(ReactstrapCardHeader)`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const NoteText = styled.div`
  color: red;
  font-style: italic;
  font-weight: normal;
  font-size: 0.9rem;
`;

const DoctorChargesTable = ({ hospitalId, doctorId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { loading, error, data } = useQuery(GET_DOCTOR_CHARGES, {
    variables: { hospitalId, doctorId },
    skip: !hospitalId || !doctorId,
    fetchPolicy: 'network-only'
  });

  if (!hospitalId || !doctorId) return null;

  const toggle = () => setIsOpen(!isOpen);

  return (
    <Card>
      <Header onClick={toggle}>
        <HeaderTitle>
          {isOpen ? (
            <>
              <FaMinus size={12} />
              Consultation Charges (Click to Minimize)
            </>
          ) : (
            <>
              <FaPlus size={12} />
              Consultation Charges (Click to Expand)
            </>
          )}
        </HeaderTitle>
        {data &&
          data.getDoctorCharges &&
          data.getDoctorCharges.length > 0 &&
          isOpen && (
            <NoteText>
              Note: Last synced on{' '}
              {moment(data.getDoctorCharges[0].createdAt).format(
                'DD-MMM hh:mm A'
              )}
            </NoteText>
          )}
      </Header>
      <Collapse isOpen={isOpen}>
        <CardBody>
          {loading && <Spinner color="primary" />}
          {error && <Alert color="danger">Failed to load charges</Alert>}
          {data && data.getDoctorCharges && data.getDoctorCharges.length > 0 ? (
            <>
              <Table bordered responsive>
                <thead>
                  <tr>
                    <th>Procedure</th>
                    {data.getDoctorCharges.map((charge, index) => (
                      <th key={index}>{charge.serviceType}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>OP Charges</td>
                    {data.getDoctorCharges.map((charge, index) => (
                      <td key={index}>
                        <RupeeContainer>
                          <FaRupeeSign size={11} />
                          {parseFloat(charge.charges)
                            .toFixed(2)
                            .replace(/\.00$/, '')}
                        </RupeeContainer>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td>Medical Records & Digitization Charges</td>
                    {data.getDoctorCharges.map((charge, index) => (
                      <td key={index}>
                        <RupeeContainer>
                          <FaRupeeSign size={11} />
                          {parseFloat(charge.additionalCharges)
                            .toFixed(2)
                            .replace(/\.00$/, '')}
                        </RupeeContainer>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td>Total (Without Registration charges)</td>
                    {data.getDoctorCharges.map((charge, index) => (
                      <td key={index}>
                        <RupeeContainer>
                          <FaRupeeSign size={11} />
                          {(
                            parseFloat(charge.charges) +
                            parseFloat(charge.additionalCharges || 0)
                          )
                            .toFixed(2)
                            .replace(/\.00$/, '')}
                        </RupeeContainer>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>
            </>
          ) : (
            !loading && <span>No charges Configured in LT</span>
          )}
        </CardBody>
      </Collapse>
    </Card>
  );
};

DoctorChargesTable.propTypes = {
  hospitalId: PropTypes.number.isRequired,
  doctorId: PropTypes.number.isRequired
};

export default DoctorChargesTable;
